import { useMutation, useQueryClient } from "@tanstack/react-query";

import httpClient from "@/config/axios";
import { useScheduleStore, type CurDate } from "@/store/scheduleStore";
import { getConfigIdForVersion } from "../service/getConfigIdForVersion";
import { toast } from "@/components/toast/CustomToast";
import {
  getInfeasibilityFromError,
  type Infeasibility,
} from "@/types/roster-infeasibility";
import { invalidateRosterCachesAndSelectLatestSchedule } from "../service/invalidateRosterCreateCaches";

export type {
  InfeasibilitySeverity,
  ViolationSample,
  PreflightIssue,
  Infeasibility,
} from "@/types/roster-infeasibility";

interface GenerateRosterParams {
  gradeStrategy: string;
  curDate: CurDate | null;
  currentConfigVer: number | null;
  isAdmin: boolean;
  selectedGroupId: string | null;
}

export interface GenerateRosterResponse {
  schedule_id?: string;
  job_id?: string;
  infeasibility?: Infeasibility | null;
}

export class RosterInfeasibleError extends Error {
  infeasibility: Infeasibility;

  constructor(infeasibility: Infeasibility) {
    super(infeasibility.summary_message_ko || "근무표를 만들 수 없습니다.");
    this.name = "RosterInfeasibleError";
    this.infeasibility = infeasibility;
  }
}

function showWarningInfeasibility(infeasibility: Infeasibility) {
  const violationText = Object.entries(infeasibility.violation_summary ?? {})
    .filter(([, value]) => value && value.count > 0)
    .map(([key, value]) => `${key} ${value?.count ?? 0}건`)
    .join(", ");

  toast.warning(infeasibility.summary_message_ko, {
    description:
      violationText ||
      infeasibility.fix_suggestions_ko.slice(0, 2).join("\n") ||
      undefined,
    duration: Infinity,
  });
}

const endPointer = () => {
  return import.meta.env.VITE_ENV === "LOCAL"
    ? `/roster_create/generate`
    : `/roster_create/async`;
};

const isLocalGenerate = () => import.meta.env.VITE_ENV === "LOCAL";

export default function useGenerateRosterMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      gradeStrategy,
      curDate: dateParam,
      currentConfigVer,
      isAdmin,
      selectedGroupId,
    }: GenerateRosterParams) => {
      const selectedAlgorithm = "cp_sat"; /* 근무표 생성 > 생성 알고리즘 */

      // 서버검증
      const configId = currentConfigVer
        ? await getConfigIdForVersion(
            { configVersion: currentConfigVer },
            { isAdmin },
            { selectedGroupId: selectedGroupId ?? "" }
          )
        : null;

      const endpoint = endPointer();

      try {
        const response = await httpClient.post<GenerateRosterResponse>(
          endpoint,
          {
            year: dateParam?.year,
            month: dateParam?.month,
            algorithm: selectedAlgorithm,
            config_id: configId,
            grade_strategy: gradeStrategy,
          }
        );

        const infeasibility = response?.infeasibility;
        if (infeasibility?.severity === "warning") {
          showWarningInfeasibility(infeasibility);
        }

        return response;
      } catch (error) {
        const infeasibility = getInfeasibilityFromError(error);
        if (infeasibility?.severity === "blocking") {
          throw new RosterInfeasibleError(infeasibility);
        }
        throw error;
      }
    },
    onSuccess: async (_data, variables) => {
      if (!isLocalGenerate()) return;

      const year = variables.curDate?.year;
      const month = variables.curDate?.month;
      if (year != null && month != null) {
        await invalidateRosterCachesAndSelectLatestSchedule(
          queryClient,
          {
            year,
            month,
            selectedGroupId: variables.selectedGroupId,
            isAdmin: variables.isAdmin,
          },
          useScheduleStore.getState().setCurScheduleInfo
        );
      }

      alert("generate Success");
    },
  });
}
